import { FC, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useOsConfig } from '../../../hooks/os'
import { saveOsConfig } from '../../../Service/os'
import { Button, Card, Field, Input, PageSubtitle, PageTitle } from '../styles'
import { MoneyInput } from '../MoneyInput'
import { DEFAULT_CFG } from '../osCalc'

interface CfgForm {
  pisoCache: number
  horasPiso: number
  valorHoraExtra: number
  kmValor: number
  alimLocal: number
  alimLocalMinExtras: number
  alimViagemDia: number
  divisorLeitor: number
  // Importação P2
  leitorOlcEmpresa: number
  leitorRosembergPct: number
  equipValorLeitor: number
  equipValorSmart: number
  equipValorNetbook: number
  equipValorNotebook: number
  equipValorTef: number
  equipValorEpson: number
  equipValorZebra: number
  equipValorCelular: number
  equipValorCarregador: number
}

const DEFAULT_FORM: CfgForm = {
  ...DEFAULT_CFG,
  leitorOlcEmpresa: 120,
  leitorRosembergPct: 70,
  equipValorLeitor: 0,
  equipValorSmart: 0,
  equipValorNetbook: 0,
  equipValorNotebook: 0,
  equipValorTef: 0,
  equipValorEpson: 0,
  equipValorZebra: 0,
  equipValorCelular: 0,
  equipValorCarregador: 0,
}

export const Config: FC = () => {
  const { config, mutate } = useOsConfig()
  const [form, setForm] = useState<CfgForm>(DEFAULT_FORM)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (config) {
      setForm({
        pisoCache: Number(config.pisoCache),
        horasPiso: config.horasPiso,
        valorHoraExtra: Number(config.valorHoraExtra),
        kmValor: Number(config.kmValor),
        alimLocal: Number(config.alimLocal),
        alimLocalMinExtras: config.alimLocalMinExtras,
        alimViagemDia: Number(config.alimViagemDia),
        divisorLeitor: config.divisorLeitor,
        leitorOlcEmpresa: Number(config.leitorOlcEmpresa ?? 120),
        leitorRosembergPct: Number(config.leitorRosembergPct ?? 70),
        equipValorLeitor: Number(config.equipValorLeitor ?? 0),
        equipValorSmart: Number(config.equipValorSmart ?? 0),
        equipValorNetbook: Number(config.equipValorNetbook ?? 0),
        equipValorNotebook: Number(config.equipValorNotebook ?? 0),
        equipValorTef: Number(config.equipValorTef ?? 0),
        equipValorEpson: Number(config.equipValorEpson ?? 0),
        equipValorZebra: Number(config.equipValorZebra ?? 0),
        equipValorCelular: Number(config.equipValorCelular ?? 0),
        equipValorCarregador: Number(config.equipValorCarregador ?? 0),
      })
    }
  }, [config])

  const set = (k: keyof CfgForm, v: string | number) =>
    setForm((f) => ({ ...f, [k]: Number(v) || 0 }))

  const save = async () => {
    setSaving(true)
    try {
      await saveOsConfig(form)
      toast.success('Nova vigência salva')
      await mutate()
    } catch {
      toast.error('Erro ao salvar configuração')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <PageTitle>Configurações de OS</PageTitle>
          <PageSubtitle>
            Tabela de valores. Alterações criam uma nova vigência; OS já aprovadas mantêm o snapshot.
          </PageSubtitle>
        </div>
        <Button onClick={save} disabled={saving}>Salvar alterações</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Cachê do Técnico</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Field><label>Piso (R$)</label><MoneyInput value={form.pisoCache} onChange={(v) => set('pisoCache', v)} /></Field>
            <Field><label>Horas do piso</label><Input type="number" value={form.horasPiso} onChange={(e) => set('horasPiso', e.target.value)} /></Field>
            <Field><label>Hora extra (R$)</label><MoneyInput value={form.valorHoraExtra} onChange={(v) => set('valorHoraExtra', v)} /></Field>
          </div>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 8 }}>A tolerância de 30 min é regra fixa do sistema.</div>
        </Card>

        <Card>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Deslocamento</div>
          <Field style={{ maxWidth: 200 }}><label>Valor por Km (R$)</label><MoneyInput value={form.kmValor} onChange={(v) => set('kmValor', v)} /></Field>
        </Card>

        <Card>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Alimentação</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Field><label>Local auto (R$)</label><MoneyInput value={form.alimLocal} onChange={(v) => set('alimLocal', v)} /></Field>
            <Field><label>Horas extras p/ acionar</label><Input type="number" value={form.alimLocalMinExtras} onChange={(e) => set('alimLocalMinExtras', e.target.value)} /></Field>
            <Field><label>Diária viagem (R$)</label><MoneyInput value={form.alimViagemDia} onChange={(v) => set('alimViagemDia', v)} /></Field>
          </div>
        </Card>

        <Card>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Leitores</div>
          <Field style={{ maxWidth: 200 }}><label>Divisor (ingressos / leitor)</label><Input type="number" value={form.divisorLeitor} onChange={(e) => set('divisorLeitor', e.target.value)} /></Field>
        </Card>

        <Card>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Importação P2 — Custo empresa do leitor</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field><label>OLC — valor por leitor (R$)</label><MoneyInput value={form.leitorOlcEmpresa} onChange={(v) => set('leitorOlcEmpresa', v)} /></Field>
            <Field><label>Rosemberg — % sobre o produtor</label><Input type="number" value={form.leitorRosembergPct} onChange={(e) => set('leitorRosembergPct', e.target.value)} /></Field>
          </div>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 8 }}>Leitor BD não gera lançamento na visão empresa.</div>
        </Card>

        <Card style={{ gridColumn: '1 / -1' }}>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Importação P2 — Equipamentos para retorno (valor por unidade)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
            <Field><label>Leitor (R$)</label><MoneyInput value={form.equipValorLeitor} onChange={(v) => set('equipValorLeitor', v)} /></Field>
            <Field><label>Smart (R$)</label><MoneyInput value={form.equipValorSmart} onChange={(v) => set('equipValorSmart', v)} /></Field>
            <Field><label>Netbook (R$)</label><MoneyInput value={form.equipValorNetbook} onChange={(v) => set('equipValorNetbook', v)} /></Field>
            <Field><label>Notebook (R$)</label><MoneyInput value={form.equipValorNotebook} onChange={(v) => set('equipValorNotebook', v)} /></Field>
            <Field><label>TEF (R$)</label><MoneyInput value={form.equipValorTef} onChange={(v) => set('equipValorTef', v)} /></Field>
            <Field><label>Epson (R$)</label><MoneyInput value={form.equipValorEpson} onChange={(v) => set('equipValorEpson', v)} /></Field>
            <Field><label>Zebra (R$)</label><MoneyInput value={form.equipValorZebra} onChange={(v) => set('equipValorZebra', v)} /></Field>
            <Field><label>Celular (R$)</label><MoneyInput value={form.equipValorCelular} onChange={(v) => set('equipValorCelular', v)} /></Field>
            <Field><label>Carregador (R$)</label><MoneyInput value={form.equipValorCarregador} onChange={(v) => set('equipValorCarregador', v)} /></Field>
          </div>
        </Card>
      </div>
    </>
  )
}

export default Config
